// 我的相册 — 接 base-service /picture/printscreen_list
// 反编译参考：com.ulucu.view.fragment.UserPictureFragment + CStoreManager.requestPicList
// 路由：/m/album
import { useEffect, useState } from 'react';
import { App as AntdApp, Button, Empty, Modal, Spin, Tag } from 'antd';
import { DeleteOutlined, EyeOutlined, LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deleteAlbumPics, fetchAlbumList, type AlbumGroup, type AlbumItem } from '../api/album.js';
import { colors } from '../theme/colors.js';
import MobileHeader from '../components/MobileHeader.js';
import { useRegion } from '../hooks/useRegion.js';

const PRIMARY = colors.primary.bg;
const PRIMARY_TEXT = colors.primary.text;
const PAGE_SIZE = 80;

/** 本地（Player 截图）存档 */
const LOCAL_KEY = 'ulucu.album';
interface LocalShot {
	url: string;          // dataURL or blob URL
	ts: number;           // unix ms
	label?: string;
}

export default function Album() {
	const region = useRegion();
	return region === 'apac' ? <NativeAlbumParity /> : <AlbumClassic />;
}

function AlbumClassic() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { message, modal } = AntdApp.useApp();

	const [groups, setGroups] = useState<AlbumGroup[] | null>(null);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [editing, setEditing] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [localShots, setLocalShots] = useState<LocalShot[]>([]);
	const [preview, setPreview] = useState<LocalShot | null>(null);

	// 拉本地截图（Player 保存的 dataURL）
	useEffect(() => {
		try {
			const raw = localStorage.getItem(LOCAL_KEY);
			if (raw) setLocalShots(JSON.parse(raw));
		} catch {
			/* ignore */
		}
	}, []);

	const loadPage = (p: number, replace = false) => {
		setLoading(true);
		setError(null);
		fetchAlbumList({ page: p, limit: PAGE_SIZE })
			.then((data) => {
				if (replace) {
					setGroups(data);
				} else {
					// 合并：同一天追加 content
					setGroups((prev) => mergeGroups(prev ?? [], data));
				}
				setHasMore(data.length > 0 && data.some((g) => g.content.length >= 1));
				// 简单判定：返回为空 → 没更多
				if (data.length === 0) setHasMore(false);
			})
			.catch((e) => setError((e as Error).message ?? 'load failed'))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		loadPage(1, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onLoadMore = () => {
		if (loading || !hasMore) return;
		const next = page + 1;
		setPage(next);
		loadPage(next);
	};

	const toggleSel = (pic_id: string) => {
		setSelected((prev) => {
			const n = new Set(prev);
			if (n.has(pic_id)) n.delete(pic_id);
			else n.add(pic_id);
			return n;
		});
	};

	const exitEditing = () => {
		setEditing(false);
		setSelected(new Set());
	};

	const onDelete = () => {
		if (selected.size === 0) {
			message.info(t('album.select_first'));
			return;
		}
		modal.confirm({
			title: t('album.confirm_delete', { n: selected.size }),
			okText: t('album.delete'),
			cancelText: t('common.cancel'),
			okButtonProps: { danger: true },
			onOk: async () => {
				const ok = await deleteAlbumPics(Array.from(selected));
				if (ok) {
					message.success(t('album.deleted'));
					exitEditing();
					loadPage(1, true);
					setPage(1);
				} else {
					message.error(t('album.delete_failed'));
				}
			},
		});
	};

	const removeLocalShot = (shot: LocalShot) => {
		modal.confirm({
			title: t('album.confirm_delete', { n: 1 }),
			okText: t('album.delete'),
			cancelText: t('common.cancel'),
			okButtonProps: { danger: true },
			onOk: () => {
				const next = localShots.filter((item) => item.ts !== shot.ts);
				setLocalShots(next);
				try {
					localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
				} catch {
					/* ignore */
				}
				message.success(t('album.deleted'));
			},
		});
	};

	const totalRemote = (groups ?? []).reduce((s, g) => s + g.content.length, 0);
	const totalLocal = localShots.length;

	return (
		<div style={{ background: colors.bg.page, minHeight: '100vh' }}>
			<MobileHeader
				title={t('mtab.profile.album')}
				onBack={() => navigate(-1)}
				extra={editing ? t('common.cancel') : totalRemote > 0 ? t('album.edit') : undefined}
				onExtraClick={editing ? exitEditing : () => setEditing(true)}
			/>

			{/* 编辑工具栏 */}
			{editing && (
				<div
					role="toolbar"
					aria-label={t('album.edit')}
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '8px 12px',
						background: colors.primary.soft,
						borderBottom: `1px solid ${colors.primary.softBorder}`,
					}}
				>
					<span aria-live="polite" style={{ fontSize: 13, color: colors.text.secondary }}>
						{t('album.selected_n', { n: selected.size })}
					</span>
					<Button
						type="primary"
						danger
						icon={<DeleteOutlined />}
						onClick={onDelete}
						disabled={selected.size === 0}
						style={{ minHeight: 44 }}
					>
						{t('album.delete')}
					</Button>
				</div>
			)}

			{/* 内容 */}
			{!groups && loading && (
				<div style={{ textAlign: 'center', padding: 60 }}>
					<Spin />
				</div>
			)}
			{error && !groups && (
				<div role="alert" style={{ padding: 24, textAlign: 'center', color: colors.status.dangerText }}>{error}</div>
			)}
			{groups && totalRemote === 0 && totalLocal === 0 && (
				<div style={{ padding: '60px 0' }}>
					<Empty description={t('album.empty')} />
				</div>
			)}

			{/* 本地截图组 */}
			{totalLocal > 0 && (
				<>
					<DateHeader label={t('album.local')} count={totalLocal} />
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(min(110px, 100%), 1fr))',
							gap: 6,
							padding: 6,
						}}
					>
						{localShots.map((s) => (
							<div
								role="button"
								tabIndex={0}
								key={s.ts}
								onClick={() => setPreview(s)}
								onKeyDown={(event) => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault();
										setPreview(s);
									}
								}}
								style={{
									display: 'block',
									position: 'relative',
									aspectRatio: '1 / 1',
									background: '#000',
									borderRadius: 4,
									overflow: 'hidden',
									cursor: 'pointer',
								}}
							>
								<img
									src={s.url}
									alt={s.label ?? ''}
									style={{ width: '100%', height: '100%', objectFit: 'cover' }}
								/>
								<span
									aria-hidden="true"
									style={{
										position: 'absolute',
										left: 6,
										bottom: 6,
										width: 26,
										height: 26,
										borderRadius: 13,
										background: 'rgba(0,0,0,0.5)',
										color: '#fff',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<EyeOutlined />
								</span>
								<button
									type="button"
									aria-label={t('album.delete')}
									onClick={(event) => {
										event.stopPropagation();
										removeLocalShot(s);
									}}
									style={{
										position: 'absolute',
										top: 6,
										right: 6,
										width: 28,
										height: 28,
										border: 0,
										borderRadius: 14,
										background: 'rgba(0,0,0,0.52)',
										color: '#fff',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<DeleteOutlined />
								</button>
							</div>
						))}
					</div>
				</>
			)}

			{/* API 截图（按日期分组） */}
			{groups &&
				groups.map((g) =>
					g.content.length === 0 ? null : (
						<div key={g.time}>
							<DateHeader label={g.time} count={g.content.length} />
							<div
								style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fill, minmax(min(110px, 100%), 1fr))',
									gap: 6,
									padding: 6,
								}}
							>
								{g.content.map((it) => (
									<RemoteThumb
										key={it.pic_id}
										item={it}
										editing={editing}
										selected={selected.has(it.pic_id)}
										onToggle={() => toggleSel(it.pic_id)}
									/>
								))}
							</div>
						</div>
					),
				)}

			{/* 加载更多：仅当当前已显示数量 ≥ PAGE_SIZE 才出现（首页满了 → 可能还有更多） */}
			{groups && totalRemote >= PAGE_SIZE && hasMore && (
				<button
					type="button"
					className="a11y-button"
					onClick={onLoadMore}
					disabled={loading}
					style={{
						display: 'block',
						margin: '0 auto',
						textAlign: 'center',
						padding: 16,
						minHeight: 44,
						color: loading ? colors.text.disabled : PRIMARY_TEXT,
						fontSize: 14,
						fontWeight: 500,
						cursor: loading ? 'default' : 'pointer',
					}}
				>
					{loading ? <Spin size="small" /> : t('album.load_more')}
				</button>
			)}
			{groups && totalRemote > PAGE_SIZE && !hasMore && (
				<div style={{ textAlign: 'center', padding: 16, color: colors.text.tertiary, fontSize: 12 }}>
					{t('album.no_more')}
				</div>
			)}
			<Modal
				open={!!preview}
				footer={null}
				onCancel={() => setPreview(null)}
				centered
				width="min(92vw, 760px)"
				styles={{ body: { padding: 0, background: '#000' } }}
			>
				{preview && (
					<img
						src={preview.url}
						alt={preview.label ?? ''}
						style={{ display: 'block', width: '100%', maxHeight: '78vh', objectFit: 'contain', background: '#000' }}
					/>
				)}
			</Modal>
		</div>
	);
}

function DateHeader({ label, count }: { label: string; count: number }) {
	return (
		<h2
			style={{
				display: 'flex',
				alignItems: 'baseline',
				gap: 8,
				padding: '12px 12px 6px',
				margin: 0,
				color: colors.text.secondary,
				fontSize: 13,
				fontWeight: 500,
			}}
		>
			<span style={{ color: colors.text.primary, fontWeight: 500, fontSize: 14 }}>{label}</span>
			<Tag color="default" style={{ fontSize: 11, padding: '0 6px', margin: 0 }}>
				{count}
			</Tag>
		</h2>
	);
}

function RemoteThumb({
	item,
	editing,
	selected,
	onToggle,
}: {
	item: AlbumItem;
	editing: boolean;
	selected: boolean;
	onToggle: () => void;
}) {
	const onClick = () => {
		if (editing) {
			onToggle();
			return;
		}
		// 点开放大查看
		window.open(item.pic_path, '_blank', 'noopener,noreferrer');
	};
	const altText = item.create_time
		? `${item.create_time}`
		: 'Album screenshot';
	return (
		<button
			type="button"
			className="a11y-button"
			onClick={onClick}
			aria-pressed={editing ? selected : undefined}
			aria-label={editing ? altText : `${altText} (open)`}
			style={{
				position: 'relative',
				aspectRatio: '1 / 1',
				background: '#000',
				borderRadius: 4,
				overflow: 'hidden',
				cursor: 'pointer',
				outline: selected ? `3px solid ${PRIMARY}` : 'none',
				outlineOffset: -3,
			}}
		>
			<img
				src={item.pic_path}
				alt={altText}
				loading="lazy"
				onError={(e) => {
					(e.currentTarget as HTMLImageElement).style.opacity = '0.3';
				}}
				style={{ width: '100%', height: '100%', objectFit: 'cover' }}
			/>
			{editing && (
				<span
					aria-hidden="true"
					style={{
						position: 'absolute',
						top: 6,
						right: 6,
						width: 22,
						height: 22,
						borderRadius: '50%',
						background: selected ? PRIMARY : 'rgba(255,255,255,0.92)',
						color: '#fff',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: 14,
						fontWeight: 700,
					}}
				>
					{selected ? '✓' : ''}
				</span>
			)}
		</button>
	);
}

/** 合并已有 groups + 新 page（按 time 字段去重 + 追加 content） */
function mergeGroups(prev: AlbumGroup[], next: AlbumGroup[]): AlbumGroup[] {
	const map = new Map<string, AlbumGroup>();
	for (const g of prev) map.set(g.time, { ...g, content: [...g.content] });
	for (const g of next) {
		const cur = map.get(g.time);
		if (cur) {
			// 按 pic_id 去重
			const seen = new Set(cur.content.map((c) => c.pic_id));
			cur.content.push(...g.content.filter((c) => !seen.has(c.pic_id)));
		} else {
			map.set(g.time, { ...g, content: [...g.content] });
		}
	}
	return Array.from(map.values());
}

function NativeAlbumParity() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { message, modal } = AntdApp.useApp();
	const [tab, setTab] = useState<'picture' | 'video'>('picture');
	const [groups, setGroups] = useState<AlbumGroup[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [editing, setEditing] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [preview, setPreview] = useState<AlbumItem | null>(null);

	const total = (groups ?? []).reduce((sum, group) => sum + group.content.length, 0);

	const loadPictures = () => {
		setLoading(true);
		void fetchAlbumList({ page: 1, limit: PAGE_SIZE })
			.then(setGroups)
			.catch(() => setGroups([]))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		if (tab === 'picture') loadPictures();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab]);

	const toggleSelection = (picId: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(picId)) next.delete(picId);
			else next.add(picId);
			return next;
		});
	};

	const exitEditing = () => {
		setEditing(false);
		setSelected(new Set());
	};

	const onDeleteSelected = () => {
		if (selected.size === 0) {
			message.info(t('album.select_first'));
			return;
		}
		modal.confirm({
			title: t('album.confirm_delete', { n: selected.size }),
			okText: t('native.common.confirm'),
			cancelText: t('native.common.cancel'),
			okButtonProps: { danger: true },
			onOk: async () => {
				const ok = await deleteAlbumPics(Array.from(selected));
				if (ok) {
					message.success(t('album.deleted'));
					exitEditing();
					loadPictures();
				} else {
					message.error(t('album.delete_failed'));
				}
			},
		});
	};

	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<header
				style={{
					height: 48,
					background: '#fff',
					borderBottom: '1px solid #eeeeee',
					display: 'grid',
					gridTemplateColumns: '56px minmax(0, 1fr) 64px',
					alignItems: 'center',
					position: 'sticky',
					top: 0,
					zIndex: 10,
				}}
			>
				<button
					type="button"
					aria-label={t('common.back')}
					onClick={() => navigate(-1)}
					style={{ height: 48, border: 0, background: 'transparent', color: '#30343b', fontSize: 18 }}
				>
					<LeftOutlined />
				</button>
				<div
					role="tablist"
					aria-label={t('mtab.profile.album')}
					style={{
						width: 156,
						height: 32,
						margin: '0 auto',
						border: '1px solid #ff8a34',
						borderRadius: 18,
						overflow: 'hidden',
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						background: '#fff',
					}}
				>
					<NativeAlbumTabButton label={t('native.common.picture')} active={tab === 'picture'} onClick={() => { setTab('picture'); exitEditing(); }} />
					<NativeAlbumTabButton label={t('native.common.video')} active={tab === 'video'} onClick={() => { setTab('video'); exitEditing(); }} />
				</div>
				<button
					type="button"
					disabled={tab !== 'picture' || total === 0}
					onClick={() => (editing ? exitEditing() : setEditing(true))}
					style={{
						height: 48,
						border: 0,
						background: 'transparent',
						color: tab !== 'picture' || total === 0 ? '#c8c8c8' : '#30343b',
						fontSize: 15,
					}}
				>
					{editing ? t('native.common.complete') : t('native.common.edit')}
				</button>
			</header>
			{editing && (
				<div style={{ height: 50, background: '#fff7f0', borderBottom: '1px solid #ffe1cc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
					<span style={{ color: '#666', fontSize: 14 }}>{t('album.selected_n', { n: selected.size })}</span>
					<Button danger type="primary" icon={<DeleteOutlined />} disabled={selected.size === 0} onClick={onDeleteSelected}>
						{t('album.delete')}
					</Button>
				</div>
			)}
			{tab === 'video' ? (
				<NativeNoData onRefresh={() => undefined} />
			) : loading && groups == null ? (
				<div style={{ paddingTop: 92, textAlign: 'center' }}><Spin /></div>
			) : total === 0 ? (
				<NativeNoData onRefresh={loadPictures} />
			) : (
				<div style={{ paddingBottom: 24 }}>
					{(groups ?? []).map((group) => group.content.length === 0 ? null : (
						<section key={group.time}>
							<DateHeader label={group.time} count={group.content.length} />
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, padding: 3 }}>
								{group.content.map((item) => (
									<NativeAlbumThumb
										key={item.pic_id}
										item={item}
										editing={editing}
										selected={selected.has(item.pic_id)}
										onToggle={() => toggleSelection(item.pic_id)}
										onPreview={() => setPreview(item)}
									/>
								))}
							</div>
						</section>
					))}
				</div>
			)}
			<Modal
				open={Boolean(preview)}
				footer={null}
				onCancel={() => setPreview(null)}
				centered
				width="min(92vw, 760px)"
				styles={{ body: { padding: 0, background: '#000' } }}
			>
				{preview && (
					<img
						src={preview.pic_path}
						alt={preview.create_time ?? ''}
						style={{ display: 'block', width: '100%', maxHeight: '78vh', objectFit: 'contain', background: '#000' }}
					/>
				)}
			</Modal>
		</div>
	);
}

function NativeAlbumTabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
	return (
		<button
			type="button"
			role="tab"
			aria-selected={active}
			onClick={onClick}
			style={{
				border: 0,
				background: active ? '#ff8a34' : '#fff',
				color: active ? '#fff' : '#ff8a34',
				fontSize: 14,
				fontWeight: 500,
				cursor: 'pointer',
			}}
		>
			{label}
		</button>
	);
}

function NativeAlbumThumb({
	item,
	editing,
	selected,
	onToggle,
	onPreview,
}: {
	item: AlbumItem;
	editing: boolean;
	selected: boolean;
	onToggle: () => void;
	onPreview: () => void;
}) {
	return (
		<button
			type="button"
			onClick={editing ? onToggle : onPreview}
			aria-pressed={editing ? selected : undefined}
			style={{
				position: 'relative',
				border: 0,
				padding: 0,
				aspectRatio: '1 / 1',
				background: '#d8d8d8',
				overflow: 'hidden',
				cursor: 'pointer',
				outline: selected ? '3px solid #ff8a34' : 'none',
				outlineOffset: -3,
			}}
		>
			<img src={item.pic_path} alt={item.create_time ?? ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
			{editing && (
				<span
					aria-hidden="true"
					style={{
						position: 'absolute',
						top: 7,
						right: 7,
						width: 22,
						height: 22,
						borderRadius: '50%',
						background: selected ? '#ff8a34' : 'rgba(255,255,255,0.92)',
						color: '#fff',
						boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: 14,
						fontWeight: 700,
					}}
				>
					{selected ? '✓' : ''}
				</span>
			)}
		</button>
	);
}

function NativeAlbum() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<header
				style={{
					height: 48,
					background: '#fff',
					borderBottom: '1px solid #eeeeee',
					display: 'grid',
					gridTemplateColumns: '56px minmax(0, 1fr) 64px',
					alignItems: 'center',
					position: 'sticky',
					top: 0,
					zIndex: 10,
				}}
			>
				<button
					type="button"
					aria-label={t('common.back')}
					onClick={() => navigate(-1)}
					style={{ height: 48, border: 0, background: 'transparent', color: '#30343b', fontSize: 18 }}
				>
					<LeftOutlined />
				</button>
				<div
					role="tablist"
					aria-label={t('mtab.profile.album')}
					style={{
						width: 136,
						height: 32,
						margin: '0 auto',
						border: '1px solid #ff8a34',
						borderRadius: 18,
						overflow: 'hidden',
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						background: '#fff',
					}}
				>
					<span role="tab" aria-selected="true" aria-label={t('native.common.picture')} style={{ background: '#ff8a34' }} />
					<span role="tab" aria-selected="false" aria-label={t('native.common.video')} style={{ background: '#fff' }} />
				</div>
				<button
					type="button"
					style={{ height: 48, border: 0, background: 'transparent', color: '#30343b', fontSize: 15 }}
				>
					{t('native.common.edit')}
				</button>
			</header>
			<NativeNoData onRefresh={() => undefined} />
		</div>
	);
}

function NativeNoData({ onRefresh }: { onRefresh: () => void }) {
	const { t } = useTranslation();
	return (
		<div style={{ minHeight: 'calc(100vh - 48px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			<div style={{ transform: 'translateY(-64px)', textAlign: 'center' }}>
				<div
					aria-hidden="true"
					style={{
						width: 72,
						height: 82,
						margin: '0 auto 20px',
						borderRadius: 8,
						background: '#e6e6e6',
						position: 'relative',
					}}
				>
					<span style={{ position: 'absolute', left: 16, right: 16, top: 22, height: 5, background: '#d2d2d2' }} />
					<span style={{ position: 'absolute', left: 16, right: 16, top: 38, height: 5, background: '#d2d2d2' }} />
					<span style={{ position: 'absolute', left: 16, right: 16, top: 54, height: 5, background: '#d2d2d2' }} />
				</div>
				<div style={{ color: '#9a9a9a', fontSize: 14, lineHeight: 1.2 }}>{t('native.common.no_data')}</div>
				<button
					type="button"
					onClick={onRefresh}
					style={{ marginTop: 12, border: 0, background: 'transparent', color: '#ff8a34', fontSize: 15 }}
				>
					{t('native.common.refresh')}
				</button>
			</div>
		</div>
	);
}
